import './admin-view.css';
import { useContext, useEffect, useState, useRef } from 'react';
import { UserContext } from '../../context';
import Button from '../../components/button/button';
import { Panel, Panels } from '../../components/panel/panel';
import PageTop from '../../components/page-top/page-top';
import { auth } from '../../firebase-config';
import { getIdTokenResult } from 'firebase/auth';
import { startSupportSession } from '../../modules/support-session';
import List from '../../components/list/list';
import SubscriptionTransactions from '../subscription/subscription-transactions';
import { Timestamp } from 'firebase/firestore';
import SUBSCRIPTION_STATUS from '../../constants/payments/subscriptionStatus';

export default function AdminView() {
  const userContext = useContext(UserContext);

  const [companies, setCompanies] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const supportStarting = useRef(false);

  // Check if user is superadmin, if not, redirect to home
  useEffect(() => {
    const checkSuperadmin = async () => {
      const idTokenResult = await getIdTokenResult(auth.currentUser);
      if (!idTokenResult.claims.superadmin) {
        window.location.href = '/';
      }
    };

    if (userContext.uid) {
      checkSuperadmin();
    }
  }, [userContext.uid]);

  const emulateUser = async (userUUIDToEmulate) => {
    if (supportStarting.current) return;
    supportStarting.current = true;
    try {
      await startSupportSession(userUUIDToEmulate);
    } catch (error) {
      console.error('Error starting support login:', error);
      alert('Support-login kunne ikke startes. Prøv igen.');
    } finally {
      supportStarting.current = false;
    }
  };

  const handleFetchCompanies = async () => {
    const token = await auth.currentUser.getIdToken(true);
    const response = await fetch('/api/admin/companies', {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      alert('Error fetching companies');
      console.error('Error fetching companies:', response);
      return;
    }

    const data = await response.json();

    if (data) {
      const newCompanies = [];
      const newTransactions = [];

      data?.companies?.map((company) => {
        const employeesWithAdminOrCompanyOwner = company?.users.filter(
          (employee) =>
            employee?.isAdmin || employee.id === company?.data?.ownerUserId
        );

        newCompanies.push({
          link: `/admin/virksomhed/${company.id}`,
          id: company.id,
          fields: [
            {
              flex: 15,
              label: 'Virksomhed',
              value: (
                <div className="admin-users-list-row-group">
                  <div>
                    <span className="admin-users-list-row-name">
                      {company?.data?.name}
                    </span>
                    <span className="admin-users-list-row-small">
                      CVR: {company?.data?.vat}
                    </span>
                    <span className="admin-users-list-row-small">
                      ID: {company?.id}
                    </span>
                  </div>
                </div>
              ),
              sortBy: company?.data?.name.toLowerCase() || '',
              searchBy: company?.data?.name.toLowerCase() || '',
            },
            {
              flex: 20,
              label: 'Abonnement',
              value: (
                <div className="admin-users-list-row-group">
                  <div>
                    <span className="admin-users-list-row-name">
                      {company?.subscriptionData?.name}{' '}
                      {company?.subscriptionData?.trial ? '(Prøveperiode)' : ''}
                    </span>
                    {company?.onPaySubscriptionData?.onPayData?.data
                      ?.order_id && (
                      <span className="admin-users-list-row-small">
                        ID:{' '}
                        {
                          company?.onPaySubscriptionData?.onPayData?.data
                            ?.order_id
                        }
                      </span>
                    )}
                    {company?.subscriptionData?.nextBillingDate && (
                      <span className="admin-users-list-row-small">
                        Næste fakturering:{' '}
                        {new Date(
                          company?.subscriptionData?.nextBillingDate
                        ).toLocaleDateString('da-DK')}
                      </span>
                    )}
                  </div>
                </div>
              ),
              sortBy: company?.subscriptionData?.nextBillingDate
                ? new Date(company?.subscriptionData?.nextBillingDate)
                : '',
              searchBy: `${
                company?.subscriptionData?.name.toLowerCase() || ''
              } ${
                company?.onPaySubscriptionData?.onPayData?.data?.order_id || ''
              }`,
            },
            {
              flex: 8,
              label: 'Status',
              value: (
                <div style={{ whiteSpace: 'normal' }}>
                  {
                    SUBSCRIPTION_STATUS.find(
                      (status) =>
                        status.value === company?.subscriptionData?.status
                    )?.label
                  }
                </div>
              ),
            },
            {
              label: 'Oprettet d.',
              flex: 8,
              value: new Date(company?.createTime)?.toLocaleDateString(
                'da-DK',
                {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                }
              ),
              sortBy: company?.createTime,
              searchBy: company?.createTime,
            },
            {
              label: 'Medarbejdere',
              flex: 10,
              value: (
                <div className="admin-users-list-row-group">
                  <div>
                    <span className="admin-users-list-row-name">
                      {company?.users?.length}
                    </span>
                    <span className="admin-users-list-row-small">
                      M. tidsreg.:{' '}
                      {
                        company?.users?.filter((user) => user.hasMobileAccess)
                          .length
                      }
                    </span>
                    <span className="admin-users-list-row-small">
                      M. admin.: {employeesWithAdminOrCompanyOwner.length}
                    </span>
                  </div>
                </div>
              ),
              sortBy: company.users.length,
              searchBy: company.users.length,
            },
            {
              label: 'Udstyr',
              flex: 5,
              value: company.hasEquipment ? 'Ja' : 'Nej',
              sortBy: company.hasEquipment,
              searchBy: company.hasEquipment,
            },
            {
              label: 'Log på',
              flex: 8,
              value: (
                <Button
                  className="small"
                  buttonStyle="text-link"
                  onClick={() => {
                    emulateUser(company?.createdBy?.id);
                  }}
                >
                  Log på
                </Button>
              ),
            },
          ],
          filterData: {
            ID: company.id,
            Virksomhed: company?.data?.name.toLowerCase(),
            Abonnement: `${company?.subscriptionData?.name?.toLowerCase()} ${
              company?.onPaySubscriptionData?.onPayData?.data?.order_id
            }`,
            Status: company?.subscriptionData?.status,
            'Oprettet d.': company.createTime,
            Medarbejdere: company.users.length,
            Udstyr: company.hasEquipment,
          },
        });

        newTransactions.push(
          company?.transactions?.map((transaction) => ({
            id: transaction.id,
            data: {
              ...transaction.data,
              date_created: new Timestamp(
                transaction.data.date_created._seconds,
                transaction.data.date_created._nanoseconds
              ),
            },
          }))
        );
      });

      setCompanies(newCompanies);
      setTransactions(newTransactions.flat());
    }
  };

  useEffect(() => {
    handleFetchCompanies();
  }, []);

  return (
    <div className="main-layout">
      <PageTop title="Admin" />
      <Panels>
        {companies && companies.length > 0 && (
          <Panel noPadding header={<h4>Virksomheder</h4>} headerTop>
            <List
              showSearch
              head={[
                { label: 'Virksomhed', flex: 15, sortable: true },
                { label: 'Abonnement', flex: 20, sortable: true },
                { label: 'Status', flex: 8, sortable: true },
                { label: 'Oprettet d.', flex: 8, sortable: true },
                { label: 'Medarbejdere', flex: 10, sortable: true },
                { label: 'Udstyr', flex: 5, sortable: true },
                { label: 'Log på', flex: 8 },
              ]}
              items={companies}
            />
          </Panel>
        )}
        <SubscriptionTransactions
          isSuperAdmin
          transactions={transactions?.sort((a, b) => {
            return b.data.date_created - a.data.date_created;
          })}
        />
      </Panels>
    </div>
  );
}
